import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  serverTimestamp,
  orderBy,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../../Firebaseconfig';
import Navbar from '../MainComponents/Navbar';
import Footer from '../MainComponents/Footer';
import {
  FaStar,
  FaChevronLeft,
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaShoppingBag,
  FaBoxOpen,
  FaStore,
  FaCheck,
  FaClock,
  FaRegHeart,
  FaRegCommentAlt
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useCart } from './CartContext';
import './CompanyPage.css';

/* ─── group an array of products by their `category` field ─── */
function groupByCategory(products) {
  return products.reduce((acc, product) => {
    const cat = product.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(product);
    return acc;
  }, {});
}

/* ─── single product card inside the company menu ─── */
function MenuProductCard({ product, onOrder, isOrdering, orderSuccess, isClosed }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const image =
    product.images && product.images.length > 0 && !product.images[0].startsWith('blob:')
      ? product.images[0]
      : null;

  const price = parseFloat(product.finalPrice || product.basePrice || 0).toFixed(2);

  return (
    <div className="cp-menu-card">
      <div
        className={`cp-menu-card-image ${isClosed ? 'closed' : ''}`}
        onClick={() => !isClosed && navigate(`/product/${product.id}`)}
        role="button"
        tabIndex={isClosed ? -1 : 0}
        onKeyDown={(e) => !isClosed && e.key === 'Enter' && navigate(`/product/${product.id}`)}
      >
        {image ? (
          <img src={image} alt={product.name} className="cp-menu-card-img" />
        ) : (
          <div className="cp-menu-card-no-image">
            <FaStore className="cp-menu-card-placeholder-icon" />
          </div>
        )}
        {product.featuredProduct && (
          <span className="cp-menu-card-badge">Popular</span>
        )}
        {product.inventoryStatus === 'outofstock' && (
          <span className="cp-menu-card-badge cp-menu-card-badge--oos">Out of Stock</span>
        )}
      </div>

      <div className="cp-menu-card-body">
        <div className="cp-menu-card-header">
          <h3 className="cp-menu-card-name">{product.name || 'Unnamed Item'}</h3>
          <span className="cp-menu-card-price">${price}</span>
        </div>

        {product.description && (
          <p className="cp-menu-card-desc">{product.description}</p>
        )}

        <div className="cp-menu-card-footer">
          {orderSuccess === product.id ? (
            <div className="cp-menu-qty-controls" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', background: 'rgba(0,0,0,0.2)', padding: '12px 20px', borderRadius: '10px', width: '100%', boxSizing: 'border-box' }}>
              <FaCheck color="#4ade80" /> <span style={{ fontSize: '13.5px', fontWeight: '700', color: '#4ade80' }}>Added to cart</span>
            </div>
          ) : (
            <button
              className="cp-menu-card-btn"
              onClick={() => onOrder(product)}
              disabled={isClosed || isOrdering === product.id || product.inventoryStatus === 'outofstock'}
              type="button"
              style={isClosed ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
            >
              <FaShoppingBag className="cp-menu-card-btn-icon" />
              {isClosed ? 'Closed' : isOrdering === product.id ? 'Adding…' : 'Add to Cart'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── MAIN COMPONENT ─── */
export default function CompanyPage() {
  const { t } = useTranslation();
  const { companyId } = useParams();
  const navigate = useNavigate();

  const [company, setCompany] = useState(null);
  const [companyLoading, setCompanyLoading] = useState(true);
  const [companyNotFound, setCompanyNotFound] = useState(false);

  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);

  const [currentUser, setCurrentUser] = useState(null);

  const { addToCart, cartItems, cartCompany, cartTotal } = useCart();

  /* Per-product order state: id of item currently ordering, or id of last success */
  const [isOrdering, setIsOrdering] = useState(null);   // product.id | null
  const [orderSuccess, setOrderSuccess] = useState(null); // product.id | null

  /* Reviews state */
  const [reviews, setReviews] = useState([]);
  const [newReview, setNewReview] = useState('');
  const [newRating, setNewRating] = useState(0); 
  const [hoverRating, setHoverRating] = useState(0);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [reviewSort, setReviewSort] = useState('recent'); // 'recent' | 'top'





  /* ── Auth listener ── */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => setCurrentUser(user));
    return () => unsub();
  }, []);

  /* ── Fetch company doc (one-time getDocs) ────────────────────────────────
   *  Choice: getDocs, not onSnapshot.
   *  Rationale: A company's public profile (name, location, logo, bio) is
   *  infrequently updated. Real-time updates here would add a persistent
   *  listener with negligible value. Products, however, are actively managed
   *  (inventory, availability) and *do* benefit from live updates, so those
   *  use onSnapshot.
   * ─────────────────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!companyId) return;

    const fetchCompany = async () => {
      try {
        const snap = await getDoc(doc(db, 'users', companyId));
        if (snap.exists()) {
          setCompany({ id: snap.id, ...snap.data() });
        } else {
          setCompanyNotFound(true);
        }
      } catch (err) {
        console.error('[CompanyPage] Error fetching company:', err);
        setCompanyNotFound(true);
      } finally {
        setCompanyLoading(false);
      }
    };

    fetchCompany();
  }, [companyId]);

  /* ── Fetch products for this company (real-time) ── */
  useEffect(() => {
    if (!companyId) return;

    const q = query(
      collection(db, 'products'),
      where('postedBy', '==', companyId)
    );

    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((p) => p.onlineStore !== false); // only show published products
        setProducts(data);
        setProductsLoading(false);
      },
      (err) => {
        console.error('[CompanyPage] Error fetching products:', err);
        setProductsLoading(false);
      }
    );

    return () => unsub();
  }, [companyId]);

  /* ── Fetch reviews for this company (real-time) ── */
  useEffect(() => {
    if (!companyId) return;

    const q = query(
      collection(db, 'ratings'),
      where('companyId', '==', companyId),
      orderBy('createdAt', 'desc')
    );

    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setReviews(data);
      },
      (err) => {
        console.error('[CompanyPage] Error fetching reviews:', err);
      }
    );

    return () => unsub();
  }, [companyId]);

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!currentUser) {
      alert(t('loginToReview') || 'Please log in to submit a review.');
      return;
    }
    if (!newReview.trim()) return;

    setIsSubmittingReview(true);
    try {
      await addDoc(collection(db, 'ratings'), {
        companyId,
        orderId: 'inline_review', // Indication that this review wasn't tied to a specific order
        customerId: currentUser.uid,
        customerName: currentUser.displayName || currentUser.email?.split('@')[0] || 'User',
        customerPhoto: currentUser.photoURL || '',
        comment: newReview.trim(),
        stars: newRating,
        createdAt: serverTimestamp(),
      });
      setNewReview('');
      setNewRating(0);
    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Failed to submit review.');
    } finally {
      setIsSubmittingReview(false);
    }
  };

  /* ── Scroll-reveal observer (same pattern as Orders.jsx) ── */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            obs.unobserve(entry.target);
          }
        });
      },
      { root: null, rootMargin: '0px 0px -6% 0px', threshold: 0.04 }
    );

    const els = document.querySelectorAll('.reveal, .reveal-stagger');
    els.forEach((el) => observer.observe(el));
    return () => els.forEach((el) => observer.unobserve(el));
  }, [companyLoading, productsLoading, products]);

  const handleOrder = async (product) => {
    setIsOrdering(product.id);

    // Pass the current company doc so CartContext knows who this is from
    const compData = {
      id: companyId,
      uid: companyId,
      name: companyName,
      businessName: companyName,
      photoURL: company.photoURL
    };

    const added = addToCart(product, compData, 1);

    if (added) {
      setOrderSuccess(product.id);
      setTimeout(() => setOrderSuccess(null), 2000);
    }

    setIsOrdering(null);
  };

  /* ── Loading state ── */
  if (companyLoading) {
    return (
      <div className="cp-loading-container">
        <Navbar />
        <div className="cp-spinner-box">
          <div className="cp-spinner" />
          <p>Loading restaurant…</p>
        </div>
        <Footer />
      </div>
    );
  }

  /* ── Not-found state ── */
  if (companyNotFound) {
    return (
      <div className="cp-loading-container">
        <Navbar />
        <div className="cp-spinner-box">
          <FaBoxOpen className="cp-notfound-icon" />
          <h2 className="cp-notfound-title">Restaurant not found</h2>
          <p className="cp-notfound-desc">
            This restaurant profile doesn't exist or may have been removed.
          </p>
          <Link to="/products" className="cp-notfound-btn">
            ← Browse all restaurants
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  /* ── Derived display values ── */
  const companyName =
    company.businessName ||
    `${company.firstName || ''} ${company.lastName || ''}`.trim() ||
    company.email?.split('@')[0] ||
    'Restaurant';

  const avatarLetter = companyName[0]?.toUpperCase() || '?';
  const rating = Number(company.rating || 4.8).toFixed(1);
  const grouped = groupByCategory(products);
  const categoryKeys = Object.keys(grouped).sort();

  const isClosed = (() => {
    if (!company?.openTime || !company?.closeTime) return false;
    const now = new Date();
    const currentMins = now.getHours() * 60 + now.getMinutes();
    
    const [openH, openM] = company.openTime.split(':').map(Number);
    const [closeH, closeM] = company.closeTime.split(':').map(Number);
    const openMins = openH * 60 + openM;
    const closeMins = closeH * 60 + closeM;

    if (closeMins < openMins) {
      // Closes after midnight
      return !(currentMins >= openMins || currentMins <= closeMins);
    }
    return currentMins < openMins || currentMins > closeMins;
  })();

  return (
    <div className="cp-page">
      <Navbar />

      {/* ── Back link ── */}
      <div className="cp-back-container">
        <Link to="/products" className="cp-back-link">
          <FaChevronLeft /> Back to restaurants
        </Link>
      </div>

      {/* ═══════════════════════════════════
          COMPANY HEADER
      ═══════════════════════════════════ */}
      <div className="cp-header reveal">
        {/* Banner */}
        <div className="cp-header-banner">
          {company.bannerURL || company.photoURL ? (
            <img src={company.bannerURL || company.photoURL} alt={companyName} className="cp-header-banner-img" />
          ) : (
            <div className="cp-header-banner-placeholder">
              <span className="cp-header-banner-letter">{avatarLetter}</span>
            </div>
          )}
          <div className="cp-header-banner-overlay" />
        </div>

        {/* Info card overlapping the banner */}
        <div className="cp-header-info">
          {/* Avatar */}
          <div className="cp-header-avatar-wrap">
            {company.photoURL ? (
              <img src={company.photoURL} alt={companyName} className="cp-header-avatar-img" />
            ) : (
              <div className="cp-header-avatar-placeholder">
                <span>{avatarLetter}</span>
              </div>
            )}
          </div>

          <div className="cp-header-text">
            {/* Status badges */}
            <div className="cp-header-badges">
              <span className="cp-header-badge cp-header-badge--status" style={isClosed ? { background: 'rgba(239, 68, 68, 0.12)', color: '#ef4444', borderColor: 'rgba(239,68,68,0.25)' } : {}}>
                <span className="cp-header-badge-dot" style={isClosed ? { background: '#ef4444', boxShadow: '0 0 6px #ef4444' } : {}} />
                {isClosed ? 'Closed Now' : (company.status || 'Open Now')}
              </span>
              {company.partnershipModel === 'licensed_partner' && (
                <span className="cp-header-badge cp-header-badge--licensed">✓ Licensed Partner</span>
              )}
            </div>

            <h1 className="cp-header-name">{companyName}</h1>

            {/* Info chips row */}
            <div className="cp-header-chips">
              <div className="cp-chip cp-chip--star">
                <FaStar className="cp-chip-icon" />
                <span className="cp-chip-val">{rating}</span>
                <span className="cp-chip-label">Rating</span>
              </div>

              {company.businessLocation && (
                <div className="cp-chip">
                  <FaMapMarkerAlt className="cp-chip-icon" />
                  <span className="cp-chip-label">{company.businessLocation}</span>
                </div>
              )}

              {(company.openTime || company.closeTime) && (
                <div className={`cp-chip ${isClosed ? 'cp-chip--closed' : 'cp-chip--open'}`}>
                  <FaClock className="cp-chip-icon" />
                  <span className="cp-chip-label">
                    {company.openTime || '?'} – {company.closeTime || '?'}
                  </span>
                </div>
              )}

              {(company.contactNumber || company.phone) && (
                <div className="cp-chip">
                  <FaPhone className="cp-chip-icon" />
                  <span className="cp-chip-label">{company.contactNumber || company.phone}</span>
                </div>
              )}

              {(company.businessEmail || company.email) && (
                <div className="cp-chip">
                  <FaEnvelope className="cp-chip-icon" />
                  <span className="cp-chip-label">{company.businessEmail || company.email}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════
          MENU SECTION
      ═══════════════════════════════════ */}
      <div className="cp-menu-section">
        {company.description && (
          <div className="cp-about-section reveal">
            <p className="cp-about-text">{company.description}</p>
          </div>
        )}

        <div className="cp-menu-header reveal">
          <h2 className="cp-menu-title">Menu</h2>
          <p className="cp-menu-subtitle">
            {productsLoading
              ? 'Loading items…'
              : `${products.length} item${products.length !== 1 ? 's' : ''} available`}
          </p>
        </div>

        {productsLoading ? (
          <div className="cp-state-box">
            <div className="cp-spinner" />
            <p>Loading menu…</p>
          </div>
        ) : products.length === 0 ? (
          <div className="cp-state-box">
            <FaBoxOpen className="cp-state-icon" />
            <p>No menu items published yet.</p>
            <span className="cp-state-sub">Check back soon!</span>
          </div>
        ) : (
          categoryKeys.map((cat) => (
            <div key={cat} className="cp-category-section reveal">
              <div className="cp-category-header">
                <h3 className="cp-category-title">{cat}</h3>
                <span className="cp-category-count">
                  {grouped[cat].length} item{grouped[cat].length !== 1 ? 's' : ''}
                </span>
              </div>

              <div className="cp-menu-grid reveal-stagger">
                {grouped[cat].map((product) => (
                  <MenuProductCard
                    key={product.id}
                    product={product}
                    onOrder={handleOrder}
                    isOrdering={isOrdering}
                    orderSuccess={orderSuccess}
                    isClosed={isClosed}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* ── Floating Cart Bar ── */}
      {cartItems.length > 0 && cartCompany?.id === companyId && (
        <div className="cp-floating-cart reveal" style={{ opacity: 1, transform: 'none' }}>
          <div className="cp-floating-cart-inner">
            <div className="cp-cart-info">
              <span className="cp-cart-count">{cartItems.reduce((acc, item) => acc + item.quantity, 0)}</span>
              <span className="cp-cart-label">items in cart</span>
            </div>
            <div className="cp-cart-total">
              <span style={{ fontSize: '0.85rem', color: '#94a3b8', marginRight: '8px' }}>Subtotal:</span>
              <span style={{ fontSize: '1.2rem', fontWeight: '800', color: '#fff' }}>${cartTotal.toFixed(2)}</span>
            </div>
            <button className="cp-cart-checkout-btn" onClick={() => navigate('/checkout')}>
              Go to Checkout →
            </button>
          </div>
        </div>
      )}

      <div className="commentSection cp-reviews-section reveal">

        {/* ─── Rate your experience box ─── */}
        <div className="cp-rate-box">
          <p className="cp-rate-box-label">Rate your experience</p>
          <div className="cp-rate-box-body">
            <div className="cp-rate-stars-row">
              {[1, 2, 3, 4, 5].map((star) => (
                <FaStar
                  key={star}
                  className={`cp-rate-star ${
                    star <= (hoverRating || newRating) ? 'active' : ''
                  }`}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  onClick={() => setNewRating(star)}
                />
              ))}
              <span className="cp-rate-stars-label">
                {newRating === 0 ? 'Select stars' : `${newRating} star${newRating > 1 ? 's' : ''}`}
              </span>
            </div>

            {currentUser ? (
              <form className="cp-rate-input-row" onSubmit={handleSubmitReview}>
                <div className="cp-rate-avatar">
                  {currentUser.photoURL ? (
                    <img src={currentUser.photoURL} alt="You" />
                  ) : (
                    <div className="cp-rate-avatar-placeholder">
                      {(currentUser.displayName || currentUser.email || 'U').charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <input
                  className="cp-rate-input"
                  type="text"
                  placeholder="Share your thoughts on the food..."
                  value={newReview}
                  onChange={(e) => setNewReview(e.target.value)}
                  required
                />
                <button
                  type="submit"
                  className="cp-rate-submit-btn"
                  disabled={isSubmittingReview || !newReview.trim() || newRating === 0}
                >
                  {isSubmittingReview ? '...' : 'Add Review'}
                </button>
              </form>
            ) : (
              <div className="cp-rate-login-row">
                <span>Please </span>
                <Link to="/login" className="cp-rate-login-link">log in</Link>
                <span> to leave a review.</span>
              </div>
            )}
          </div>
        </div>

        {/* ─── Community Reviews ─── */}
        <div className="cp-community-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <h2 className="cp-reviews-title">Community Reviews</h2>
            {reviews.length > 0 && (
              <div className="cp-chip cp-chip--star" style={{ background: 'rgba(255, 107, 0, 0.1)', color: '#ff6b00', border: '1px solid rgba(255, 107, 0, 0.2)' }}>
                <FaStar className="cp-chip-icon" style={{ color: '#ff6b00' }} />
                <span className="cp-chip-val" style={{ fontWeight: '700' }}>
                  {(reviews.reduce((acc, r) => acc + (r.stars || 0), 0) / reviews.length).toFixed(1)}
                </span>
                <span className="cp-chip-label" style={{ color: '#ff6b00' }}>({reviews.length})</span>
              </div>
            )}
          </div>
          <div className="cp-sort-tabs">
            <button
              type="button"
              className={`cp-sort-tab ${reviewSort === 'recent' ? 'active' : ''}`}
              onClick={() => setReviewSort('recent')}
            >Most Recent</button>
            <button
              type="button"
              className={`cp-sort-tab ${reviewSort === 'top' ? 'active' : ''}`}
              onClick={() => setReviewSort('top')}
            >Highest Rated</button>
          </div>
        </div>

        <div className="cp-reviews-list">
          {reviews.length === 0 ? (
            <p className="cp-no-reviews">No reviews yet. Be the first to comment!</p>
          ) : (
            [...reviews]
              .sort((a, b) =>
                reviewSort === 'top'
                  ? (b.stars || 0) - (a.stars || 0)
                  : (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
              )
              .map((review) => {
                const timeAgo = review.createdAt ? (() => {
                  const sec = Math.floor((Date.now() / 1000) - review.createdAt.seconds);
                  if (sec < 60) return 'just now';
                  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
                  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
                  if (sec < 604800) return `${Math.floor(sec / 86400)}d ago`;
                  if (sec < 2592000) return `${Math.floor(sec / 604800)}w ago`;
                  return new Date(review.createdAt.toDate()).toLocaleDateString();
                })() : '';

                const rName = review.customerName || 'Anonymous';

                return (
                  <div key={review.id} className="cp-review-card">
                    <div className="cp-review-card-top">
                      <div className="cp-review-left">
                        {review.customerPhoto ? (
                          <img src={review.customerPhoto} alt={rName} className="cp-review-avatar" />
                        ) : (
                          <div className="cp-review-avatar-placeholder">
                            {rName.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div className="cp-review-body">
                        <div className="cp-review-meta">
                          <div className="cp-review-name-stars">
                            <span className="cp-review-username">{rName}</span>
                            <div className="cp-review-stars">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <FaStar
                                  key={star}
                                  className={`cp-star-icon-small ${star <= (review.stars || 0) ? 'active' : ''}`}
                                />
                              ))}
                            </div>
                          </div>
                          <span className="cp-review-date">{timeAgo}</span>
                        </div>
                        {review.comment && <p className="cp-review-text">{review.comment}</p>}
                        
                        {/* Action Row */}
                        <div className="cp-review-actions">
                          <button className="cp-review-action-btn">
                            <FaRegHeart />
                            <span>{Math.abs(review.id.charCodeAt(0) * 41 % 5000) > 1000 ? (Math.abs(review.id.charCodeAt(0) * 41 % 5000) / 1000).toFixed(1) + 'k' : Math.abs(review.id.charCodeAt(0) * 41 % 5000) + 12}</span>
                          </button>
                          <button className="cp-review-action-btn">
                            <FaRegCommentAlt />
                            <span>{Math.abs(review.id.charCodeAt(review.id.length-1) * 7 % 20)} replies</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
          )}
        </div>
        
        {reviews.length > 0 && (
          <div className="cp-reviews-load-more">
            <button className="cp-load-more-btn">Load more reviews <span>&#x2304;</span></button>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
